import { useEffect } from 'react';
import { Form, Input, Select, Switch, DatePicker, Button, Row, Col } from '@/shared/ui/primitives';
import { NationalitySelect, ReferralSelect } from '@/shared/ui/entity-selects';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
];
const LANGUAGE_OPTIONS = [
  { value: 'ENGLISH', label: 'English' },
  { value: 'ARABIC', label: 'Arabic' },
  { value: 'ARABIC_AND_ENGLISH', label: 'Arabic & English' },
];
const OCCUPATION_OPTIONS = [
  { value: 'STUDENT', label: 'Student' },
  { value: 'EMPLOYEE', label: 'Employee' },
  { value: 'NO_PREFERENCE', label: 'No Preference' },
];

export default function UserForm({ initialData, onSubmit, isLoading }: any) {
  const [form] = Form.useForm();
  const { t } = useTranslation();

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        email: initialData.email || '',
        phonePrefix: initialData.phonePrefix || '',
        phoneNumber: initialData.phoneNumber || '',
        phoneCountryCode: initialData.phoneCountryCode || '',
        isPhoneVerified: initialData.isPhoneVerified ?? false,
        language: initialData.language || undefined,
        gender: initialData.gender || undefined,
        occupationType: initialData.occupationType || undefined,
        isDeleted: initialData.isDeleted ?? false,
        showAge: initialData.showAge ?? false,
        dateBrith: initialData.dateBrith ? dayjs(initialData.dateBrith) : null,
        nationalityId: initialData.nationalityId || '',
        referralId: initialData.referralId || '',
      });
    }
  }, [initialData, form]);

  const handleFinish = (values: any) => {
    const payload: Record<string, unknown> = {};
    if (values.firstName) payload.firstName = values.firstName;
    if (values.lastName) payload.lastName = values.lastName;
    if (values.email) payload.email = values.email;
    if (values.phonePrefix) payload.phonePrefix = values.phonePrefix;
    if (values.phoneNumber) payload.phoneNumber = values.phoneNumber;
    if (values.phoneCountryCode) payload.phoneCountryCode = values.phoneCountryCode;
    payload.isPhoneVerified = values.isPhoneVerified;
    if (values.language) payload.language = values.language;
    if (values.gender) payload.gender = values.gender;
    if (values.occupationType) payload.occupationType = values.occupationType;
    payload.isDeleted = values.isDeleted;
    payload.showAge = values.showAge;
    if (values.dateBrith) payload.dateBrith = values.dateBrith.format('YYYY-MM-DD');
    if (values.nationalityId) payload.nationalityId = Number(values.nationalityId);
    if (values.referralId) payload.referralId = Number(values.referralId);
    onSubmit(payload);
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} style={{ maxWidth: 600 }}>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t('users.firstName')} name="firstName">
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('users.lastName')} name="lastName">
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label={t('users.email')} name="email">
        <Input type="email" autoComplete="email" data-allstak-mask="true" data-sensitive="true" />
      </Form.Item>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label={t('users.phonePrefix')} name="phonePrefix">
            <Input placeholder="+966" autoComplete="tel-country-code" data-allstak-mask="true" data-sensitive="true" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('users.phone')} name="phoneNumber">
            <Input autoComplete="tel-national" data-allstak-mask="true" data-sensitive="true" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('users.countryCode')} name="phoneCountryCode">
            <Input placeholder="SA" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label={t('users.gender')} name="gender">
            <Select allowClear options={GENDER_OPTIONS} placeholder="—" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('users.language')} name="language">
            <Select allowClear options={LANGUAGE_OPTIONS} placeholder="—" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label={t('users.occupation')} name="occupationType">
            <Select allowClear options={OCCUPATION_OPTIONS} placeholder="—" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label={t('users.dateOfBirth')} name="dateBrith">
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t('users.nationalityId')} name="nationalityId">
            <NationalitySelect placeholder={t('users.nationalityId')} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('users.referralId')} name="referralId">
            <ReferralSelect placeholder={t('users.referralId')} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col>
          <Form.Item label={t('users.verified')} name="isPhoneVerified" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item label={t('users.showAge')} name="showAge" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item label={t('users.archived')} name="isDeleted" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={isLoading}>{t('common.save')}</Button>
      </Form.Item>
    </Form>
  );
}
